import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  HStack,
  IconButton,
  Text,
  useColorModeValue,
  Button,
  Tooltip,
  VStack,
  Icon,
  Progress,
} from "@chakra-ui/react";
import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import SelectedSegmentCard from "./SelectedSegmentCard";
import { EmptySelectedSegments } from "./EmptySelectedSegments";
import { getListStyle, getItemStyle } from "./styles";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import { API_URL } from "constants";

const SCROLL_DISTANCE = 200;

const formatDuration = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export const SelectedSegments = ({
  exportVideoError,
  exportVideoProgress,
  exportVideoResult,
  exportXmlError,
  isExportingVideo,
  isExportingXml,
  onExportVideo,
  onExportXml,
  onVideoOpen,
  selectedSegments,
  setActiveVideoId,
  setSelectedSegments,
}) => {
  const listRef = React.useRef(null);
  const gray = useColorModeValue("gray.600", "gray.400");

  const totalDuration = selectedSegments.reduce((total, segment) => {
    return total + (segment.end - segment.start);
  }, 0);

  const scroll = (direction) => {
    if (listRef.current) {
      const currentScroll = listRef.current.scrollLeft;
      const newScroll =
        direction === "left"
          ? currentScroll - SCROLL_DISTANCE
          : currentScroll + SCROLL_DISTANCE;
      listRef.current.scrollTo({
        left: newScroll,
        behavior: "smooth",
      });
    }
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const items = reorder(
      selectedSegments,
      result.source.index,
      result.destination.index
    );
    setSelectedSegments(items);
  };

  const handleDoubleClick = (segment) => {
    setActiveVideoId(segment.parentId);
    onVideoOpen();
  };

  const areSegmentsSelected = selectedSegments.length > 0;
  const isDisabled = !areSegmentsSelected || isExportingVideo;

  return (
    <Box
      bg={useColorModeValue("gray.50", "navy.900")}
      borderColor={useColorModeValue("gray.200", "navy.700")}
      borderTop="1px"
      bottom={0}
      left={0}
      minH="175px"
      position="fixed"
      px={6}
      py={2}
      right={0}
      zIndex={10}
    >
      {selectedSegments.length === 0 ? (
        <EmptySelectedSegments />
      ) : (
        <>
          <VStack align="stretch" spacing={2}>
            {!!exportVideoError && (
              <Alert size="sm" status="error">
                <AlertIcon />
                <AlertTitle mr={2}>Error:</AlertTitle>
                <AlertDescription>{exportVideoError}</AlertDescription>
              </Alert>
            )}
            {!!exportXmlError && (
              <Alert size="sm" status="error">
                <AlertIcon />
                <AlertTitle mr={2}>Error:</AlertTitle>
                <AlertDescription>{exportXmlError}</AlertDescription>
              </Alert>
            )}
            {!!isExportingVideo && (
              <Box>
                <Text fontSize="sm" mb={1}>
                  Processing video... {exportVideoProgress}%
                </Text>
                <Progress
                  colorScheme="blue"
                  hasStripe
                  isAnimated
                  size="sm"
                  value={exportVideoProgress}
                />
              </Box>
            )}
            {!!exportVideoResult && (
              <Alert size="sm" status="success">
                <AlertIcon />
                <AlertTitle mr={2}>Success!</AlertTitle>
                <AlertDescription>
                  Video processing complete! Your download should begin
                  automatically, or you can{" "}
                  <a
                    download="output_video.mp4"
                    href={`${API_URL}${exportVideoResult.video_url}`}
                    style={{ textDecoration: "underline", cursor: "pointer" }}
                  >
                    click here
                  </a>{" "}
                  to download.
                </AlertDescription>
              </Alert>
            )}
            <HStack justify="space-between" mb={2}>
              <HStack>
                <Text color={gray} fontSize="sm">
                  {selectedSegments.length} segment
                  {selectedSegments.length !== 1 ? "s" : ""} selected
                </Text>
                <Text color={gray} fontSize="sm">
                  -
                </Text>
                <Text color={gray} fontSize="sm">
                  Total duration: {formatDuration(totalDuration)}
                </Text>
              </HStack>
              <HStack>
                <Tooltip
                  isDisabled={areSegmentsSelected}
                  label="Start by selecting segments"
                  placement="bottom"
                  shouldWrapChildren
                >
                  <Button
                    colorScheme="blue"
                    isDisabled={isDisabled || isExportingVideo}
                    isLoading={isExportingXml}
                    onClick={onExportXml}
                    size="sm"
                  >
                    Export .XML
                  </Button>
                </Tooltip>
                <Tooltip
                  isDisabled={areSegmentsSelected}
                  label="Start by selecting segments"
                  placement="bottom"
                  shouldWrapChildren
                >
                  <Button
                    colorScheme="blue"
                    isDisabled={isDisabled || isExportingXml}
                    isLoading={isExportingVideo}
                    onClick={onExportVideo}
                    size="sm"
                  >
                    Export .MP4
                  </Button>
                </Tooltip>
              </HStack>
            </HStack>
            <HStack align="center" spacing={2}>
              <IconButton
                aria-label="Scroll left"
                icon={<Icon as={FaChevronLeft} />}
                onClick={() => scroll("left")}
                size="sm"
                variant="ghost"
              />
              <Box flex={1} overflow="hidden">
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable direction="horizontal" droppableId="droppable">
                    {(provided, snapshot) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={getListStyle(snapshot.isDraggingOver)}
                      >
                        <div
                          ref={listRef}
                          style={{
                            display: "flex",
                            overflowX: "auto",
                            gap: "8px",
                            padding: "0 8px",
                          }}
                        >
                          {selectedSegments.map((segment, index) => (
                            <Draggable
                              draggableId={segment.uuid}
                              index={index}
                              key={segment.uuid}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style
                                  )}
                                >
                                  <SelectedSegmentCard
                                    index={index}
                                    onDoubleClick={handleDoubleClick}
                                    previousSegments={selectedSegments.slice(
                                      0,
                                      index
                                    )}
                                    segment={segment}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </Box>
              <IconButton
                aria-label="Scroll right"
                icon={<Icon as={FaChevronRight} />}
                onClick={() => scroll("right")}
                size="sm"
                variant="ghost"
              />
            </HStack>
          </VStack>
        </>
      )}
    </Box>
  );
};
